/**
 * @file interpret/engine/callFloor.ts
 * @brief Call mode: who holds the floor, and when your mic may be heard.
 *
 * Headphones: the floor below. The Mac's speakers: no floor at all, one mic
 * like the phone (see the top of `step`); level could not tell the voices
 * apart there (DES's WhatsApp tests, 03-10-2026).
 *
 * @details
 * In Call mode the uplink carries two sources: your mic and the call's audio
 * (the other side, without our own playback). On the Mac's speakers the mic
 * also hears the call and our translations, so it is held shut while:
 * 1. **They speak and you were quiet**: the other side holds the floor. If you
 *    were already speaking, you keep it ("jo pehle bola, uski baari"); then
 *    only their leak into your mic, for as long as you both talk, gets through.
 * 2. **A translation sounds on the speakers** (theirs, or yours when "Hear my
 *    translation too" is on), plus a short tail for the room's echo. With
 *    headphones the mic stays open, so you can speak over a translation.
 *
 * The call's audio is never held: what they say during a translation is
 * still interpreted, after the one playing. Your live voice goes into the
 * call while the mic is open, but not over your own translation going in.
 *
 * We also end turns ourselves (DES 03-10-2026: the server's VAD hears both
 * sources, so their room or a quick reply kept your turn open): the moment
 * the other side takes over, or after a 1 s pause. Each source's steady
 * background is learned, so a fan is not "speech".
 *
 * Levels are RMS of float samples (0..1). On speakers, while they speak, the
 * mic counts as you only when it is clearly louder than their leak would be.
 * The thresholds want tuning on real calls.
 *
 * @author DES
 * @date 03-10-2026
 */

export type FloorHolder = "me" | "them" | null;

/** What is sounding right now. */
export type FloorPlayback = "none" | "toCall" | "here";

export type FloorInput = {
  now: number;
  /** RMS of your mic and of the call's audio. */
  mic: number;
  call: number;
  playback: FloorPlayback;
  /** Your translation also plays here ("Hear my translation too"). */
  hearOwn: boolean;
  /** Translations sound on the Mac's speakers (no headphones). */
  speakers: boolean;
};

export type FloorOutput = {
  floor: FloorHolder;
  /** Your mic into the uplink (to be interpreted). */
  micToUplink: boolean;
  /** The call's audio into the uplink (headphones; on speakers your mic
   *  already hears them). */
  callToUplink: boolean;
  /** Your mic live into the call. */
  micLive: boolean;
  /** End the current turn now (the speaker paused or the other side took over). */
  endTurn: boolean;
};

/** Speech in the mic and in the call (the call is clean digital audio). */
export const MIC_SPEECH_RMS = 0.015;
export const CALL_SPEECH_RMS = 0.015;
/**
 * Headphones: the call's audio reaches the interpreter only above this level
 * (and for CALL_GATE_HOLD_MS after), so their breathing and room (0.005-0.015
 * in DES's WhatsApp tests, 03-10-2026) is silence and the server's VAD ends
 * their turn ("uski sans ki awaz bhi yeh sunta rehta hai").
 */
export const CALL_GATE_RMS = 0.02;
export const CALL_GATE_HOLD_MS = 400;
/**
 * The same gate on your mic, in both modes: the browser's auto-gain lifts
 * breathing and room to a level the server's VAD hears as speech, so turns
 * never ended (DES: "headfone main khali nahin, system per bhi").
 */
export const MIC_GATE_RMS = 0.015;
export const MIC_GATE_HOLD_MS = 400;
/**
 * A turn longer than this ends at the next dip in sound, and at MAX_TURN_MS
 * whatever happens: loud noise on their side (a TV, children, dishes) passes
 * any level gate and kept their turn open for ever (DES's WhatsApp test,
 * 04-10-2026: "the other person's turns don't end"). A long speech is then
 * interpreted in pieces, as an interpreter would.
 */
export const SOFT_TURN_MS = 8000;
export const MAX_TURN_MS = 15000;
/** On speakers, while they talk, your mic must beat this × their level. */
export const SPEAKER_LEAK_RATIO = 0.6;
/** Speech keeps the floor this long after the last loud frame (pauses between words). */
export const HANGOVER_MS = 700;
/** Room echo after a translation stops on the speakers. */
export const PLAYBACK_TAIL_MS = 300;
/**
 * You quiet this long, and they speaking for HANDOFF_SUSTAIN_MS, hands the
 * floor over before your hangover ends. 250 ms chopped your live voice at
 * every pause between words, on their echo of you (DES's WhatsApp test,
 * 03-10-2026: "jittery, broken chunk by chunk").
 */
export const HANDOFF_QUIET_MS = 600;
export const HANDOFF_SUSTAIN_MS = 300;
/**
 * Taking the floor from them (headphones only): your mic louder than their
 * call level for this long. Their steady room / TV kept the floor theirs for ever, so your
 * mic stayed shut while you spoke (DES's WhatsApp test, 03-10-2026).
 */
export const BARGE_IN_MS = 250;
/**
 * The other side must speak this long before your paused turn is ended for
 * them. A single blip (a breath, a click, room sound lifted by auto-gain)
 * cut turns short at random, worst at the start before the background was
 * learned (DES 03-10-2026: "turn per turn khud kata hai, phir theek hota
 * hai, phir garh barh").
 */
export const TAKEOVER_MS = 300;
/**
 * A pause this long ends the turn. Was 1000; DES 04-10-2026 tried 800 to sit
 * nearer mobile's ~768 ms. If turns start cutting mid-sentence, put back 1000.
 */
export const END_SILENCE_MS = 800;
/** Speech is this many times the learned background level. */
const NOISE_MARGIN = 3;

export class CallFloor {
  private floor: FloorHolder = null;
  private lastMicAt = -Infinity;
  /** When your current stretch of speech began. */
  private micRunStart = -Infinity;
  /** When your current stretch of clearly-louder-than-them speech began. */
  private bargeStart: number | null = null;
  private lastCallAt = -Infinity;
  /** When their current stretch of speech began. */
  private callRunStart = -Infinity;
  private lastAudibleAt = -Infinity;
  /** Last frame of the call above CALL_GATE_RMS. */
  private lastCallGateAt = -Infinity;
  /** Last frame of the mic above its gate. */
  private lastMicGateAt = -Infinity;
  /** When the current turn started sending sound (null: no turn open). */
  private turnStart: number | null = null;

  /** A turn ended (the server's final arrived, or we ended it). */
  noteTurnEnded(): void {
    this.turnStart = null;
  }

  /** Cap the turn's length (both modes). */
  private capTurn(out: FloorOutput, now: number, sounding: boolean): FloorOutput {
    if (out.endTurn) {
      this.turnStart = null;
      return out;
    }
    if (sounding && this.turnStart === null) this.turnStart = now;
    if (this.turnStart === null) return out;
    const age = now - this.turnStart;
    if (age >= MAX_TURN_MS || (age >= SOFT_TURN_MS && !sounding)) {
      this.turnStart = null;
      return { ...out, endTurn: true };
    }
    return out;
  }
  /** Learned background level of each source (a fan, their room). */
  private micNoise = 0.002;
  private callNoise = 0.001;
  /** A turn that has paused but not been ended yet, and its last speech. */
  private owed: FloorHolder = null;
  private owedAt = -Infinity;

  /** How long `who` has been speaking in the current stretch. */
  private heldFor(who: "me" | "them", now: number): number {
    const start = who === "me" ? this.micRunStart : this.callRunStart;
    const last = who === "me" ? this.lastMicAt : this.lastCallAt;
    return now - last < HANGOVER_MS ? now - start : 0;
  }

  step(i: FloorInput): FloorOutput {
    // The Mac's speakers: like two people at one phone (the mobile app).
    // Your mic hears them too, so only the mic is interpreted and the server
    // tells the voices apart by language and ends the turns. No floor: level
    // alone could not tell you from their voice, their echo of you or their
    // room, and either side's turns were cut or never came (DES 03-10-2026).
    // The mic is shut only while a translation sounds here (and its echo).
    // Your live voice goes on; the call app's own echo canceller removes
    // their voice from it, as on any speaker call.
    // Mic gate (both modes), against its learned background.
    const micThreshold = Math.max(MIC_SPEECH_RMS, this.micNoise * NOISE_MARGIN);
    this.micNoise = learnNoise(this.micNoise, i.mic, micThreshold);
    if (i.mic >= Math.max(MIC_GATE_RMS, this.micNoise * NOISE_MARGIN)) this.lastMicGateAt = i.now;
    const micGate = i.now - this.lastMicGateAt < MIC_GATE_HOLD_MS;

    if (i.speakers) {
      const audibleHere = i.playback === "here" || (i.playback === "toCall" && i.hearOwn);
      if (audibleHere) this.lastAudibleAt = i.now;
      const sounding = i.now - this.lastAudibleAt < PLAYBACK_TAIL_MS;
      const toUplink = !sounding && micGate;
      return this.capTurn(
        {
          floor: null,
          micToUplink: toUplink,
          callToUplink: false,
          micLive: !sounding && i.playback !== "toCall",
          endTurn: false,
        },
        i.now,
        toUplink,
      );
    }

    const callThreshold = Math.max(CALL_SPEECH_RMS, this.callNoise * NOISE_MARGIN);
    const callSpeech = i.call >= callThreshold;
    // On speakers their voice reaches your mic too: only a clearly louder
    // mic is you.
    const micSpeech =
      i.mic >= micThreshold && !(i.speakers && callSpeech && i.mic < i.call * SPEAKER_LEAK_RATIO);
    this.callNoise = learnNoise(this.callNoise, i.call, callThreshold);
    if (micSpeech) {
      if (i.now - this.lastMicAt > HANGOVER_MS) this.micRunStart = i.now;
      this.lastMicAt = i.now;
    }
    if (callSpeech) {
      if (i.now - this.lastCallAt > HANGOVER_MS) this.callRunStart = i.now;
      this.lastCallAt = i.now;
    }
    // You, clearly over their sound (not their leak into your mic). Only with
    // headphones: on the Mac's speakers your mic hears them, and the browser's
    // auto-gain lifts that leak to speech level, so "you" cut their turns
    // short (DES 03-10-2026: "doosray banday ki baat poori nahin sunta").
    if (!i.speakers && micSpeech && i.mic > i.call) this.bargeStart ??= i.now;
    else this.bargeStart = null;
    const micActive = i.now - this.lastMicAt < HANGOVER_MS;
    const callActive = i.now - this.lastCallAt < HANGOVER_MS;

    // Release: a pause long enough, or the other side starting just after
    // you stopped (a quick reply must not run into your turn).
    if (
      this.floor === "me" &&
      (!micActive ||
        (callSpeech &&
          i.now - this.lastMicAt > HANDOFF_QUIET_MS &&
          i.now - this.callRunStart >= HANDOFF_SUSTAIN_MS))
    ) {
      this.floor = null;
      // A real turn only (not a blip): only that is ended for the other side.
      if (this.lastMicAt - this.micRunStart >= TAKEOVER_MS) {
        this.owed = "me";
        this.owedAt = this.lastMicAt;
      }
    }
    if (
      this.floor === "them" &&
      (!callActive ||
        (micSpeech && i.now - this.lastCallAt > HANDOFF_QUIET_MS) ||
        (this.bargeStart !== null && i.now - this.bargeStart >= BARGE_IN_MS))
    ) {
      const bargedIn = this.bargeStart !== null && i.now - this.bargeStart >= BARGE_IN_MS;
      // Barged in: straight to you, even though their sound goes on.
      this.floor = bargedIn ? "me" : null;
      if (this.lastCallAt - this.callRunStart >= TAKEOVER_MS) {
        this.owed = "them";
        this.owedAt = this.lastCallAt;
      }
    }
    if (this.floor === null) {
      // Both at once from silence: them, since on speakers their voice is
      // in your mic as well.
      if (callSpeech || (callActive && this.owed !== "them")) this.floor = "them";
      else if (micSpeech || (micActive && this.owed !== "me")) this.floor = "me";
    }

    // End the paused turn ourselves (the server's VAD hears the other
    // source too and can wait for ever): at once when the other side takes
    // the floor, else after a full pause. The same side resuming carries on.
    // The owner speaking again carries the same turn on, whoever holds the
    // floor for a moment.
    if ((this.owed === "me" && micSpeech) || (this.owed === "them" && callSpeech)) {
      this.owed = null;
    }
    let endTurn = false;
    if (this.owed) {
      if (this.floor === this.owed) {
        this.owed = null;
      } else if (
        i.now - this.owedAt >= END_SILENCE_MS ||
        (this.floor !== null && this.heldFor(this.floor, i.now) >= TAKEOVER_MS)
      ) {
        // The other side has really started (not a breath, a click or room
        // sound the auto-gain lifted), or the pause is a full one.
        endTurn = true;
        this.owed = null;
      }
    }

    const audible = i.playback === "here" || (i.playback === "toCall" && i.hearOwn);
    if (audible) this.lastAudibleAt = i.now;
    const speakersSounding = i.speakers && i.now - this.lastAudibleAt < PLAYBACK_TAIL_MS;

    // Headphones: your mic cannot hear them, so it is never shut for their
    // sound; the floor only decides when turns end. Shutting it on "them"
    // chopped your words whenever their room made a sound (DES's EarPods
    // test, 03-10-2026: the floor flipped every second on 0.005-0.03).
    //
    // Their floor keeps your mic out of the INTERPRETER (not out of the call):
    // your room's noise otherwise kept their turn open (DES, 04-10-2026: the
    // noise was in his own background). Speaking clearly takes the floor back
    // at once (barge-in), so your words are not lost.
    const micOpen = !speakersSounding;
    const micToInterpreter = micOpen && this.floor !== "them";
    if (i.call >= Math.max(CALL_GATE_RMS, this.callNoise * NOISE_MARGIN)) {
      this.lastCallGateAt = i.now;
    }
    const out: FloorOutput = {
      floor: this.floor,
      micToUplink: micToInterpreter && micGate,
      callToUplink: i.now - this.lastCallGateAt < CALL_GATE_HOLD_MS,
      micLive: micOpen && i.playback !== "toCall",
      endTurn,
    };
    return this.capTurn(out, i.now, out.micToUplink || out.callToUplink);
  }
}

/**
 * Background level: follows quiet frames (down fast, up slowly), and creeps
 * up even under a steady sound, so a fan is learned in about half a minute
 * while ten seconds of speech barely moves it.
 */
function learnNoise(noise: number, level: number, threshold: number): number {
  if (level < noise) return noise * 0.8 + level * 0.2;
  const rate = level < threshold ? 0.02 : 0.0005;
  return noise + (level - noise) * rate;
}
